import { patientViewIdAtom } from '@/features/patient-profile/atoms';
import {
  toAnalyticsFieldName,
  usePatientProfileSurface,
  type PatientProfileSurface,
} from '@heidi/patients';
import {
  patientDocumentUploaded,
  patientFieldEdited,
  patientSessionExpanded,
} from '@heidi/patients/contributions';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { emitPatientSurfaceEvent } from './emit';
import { resolvePatientSurfaceFlagVariant } from './flag-variant';

/**
 * Bound analytics for events fired from inside an open patient-profile view
 * (drawer or full page). Each tracker auto-injects the three cross-cutting
 * fields — `view_id` (from the atom set on open), `surface` (from context), and
 * `feature_flag_variant` — so call sites only pass the event-specific data.
 *
 * New events are Snowplow-only, so every dispatch passes
 * `options: { amplitude: false }` per the analytics routing policy.
 *
 * `surfaceOverride` is for body-level callers that sit outside the
 * {@link PatientProfileSurfaceProvider} subtree (e.g. the full-page profile's
 * own component body) and therefore can't read the surface from context. Shared
 * child components should omit it and let context resolve the surface.
 */
export const usePatientSurfaceAnalytics = (surfaceOverride?: PatientProfileSurface) => {
  const contextSurface = usePatientProfileSurface();
  const surface = surfaceOverride ?? contextSurface;
  const viewId = useAtomValue(patientViewIdAtom);
  // True only when the caller is genuinely on the patient surface: an explicit
  // surface was passed, or a profile view is open (view_id set). Guards the
  // shared upload mutation, which is also consumed outside the surface (e.g. the
  // create-patient drawer) where an emit would mislabel surface + carry no view.
  const hasSurfaceContext = Boolean(surfaceOverride) || Boolean(viewId);

  const trackSessionExpanded = useCallback(
    (args: { patientId: string; sessionId: string; isLatest: boolean }) => {
      emitPatientSurfaceEvent({
        event_name: patientSessionExpanded.name,
        props: {
          view_id: viewId ?? '',
          patient_id: args.patientId,
          session_id: args.sessionId,
          is_latest: args.isLatest,
          surface,
          feature_flag_variant: resolvePatientSurfaceFlagVariant(),
        },
      });
    },
    [surface, viewId]
  );

  const trackFieldEdited = useCallback(
    (args: { patientId: string; field: string }) => {
      emitPatientSurfaceEvent({
        event_name: patientFieldEdited.name,
        props: {
          view_id: viewId ?? '',
          patient_id: args.patientId,
          field: toAnalyticsFieldName(args.field),
          surface,
          feature_flag_variant: resolvePatientSurfaceFlagVariant(),
        },
      });
    },
    [surface, viewId]
  );

  const trackDocumentUploaded = useCallback(
    (args: {
      patientId: string;
      documentId: string;
      actor:
        | 'clinician'
        | 'assistant'
        | 'patient'
        | 'integration'
        | 'bulk_import'
        | 'other';
      fileSizeBytes: number;
      fileType: 'pdf' | 'image' | 'text' | 'other';
    }) => {
      // Skip uploads from non-surface consumers of the shared mutation so they
      // don't pollute the surface dimension / open->upload stitching.
      if (!hasSurfaceContext) return;
      emitPatientSurfaceEvent({
        event_name: patientDocumentUploaded.name,
        props: {
          view_id: viewId ?? '',
          patient_id: args.patientId,
          document_id: args.documentId,
          actor: args.actor,
          file_size_bytes: args.fileSizeBytes,
          file_type: args.fileType,
          surface,
          feature_flag_variant: resolvePatientSurfaceFlagVariant(),
        },
      });
    },
    [surface, viewId, hasSurfaceContext]
  );

  return {
    trackSessionExpanded,
    trackFieldEdited,
    trackDocumentUploaded,
  };
};
